import { get, post, put, del } from '../../helpers/api';

// The admin promotions API lives on the nestjs admin service (/admin/*),
// which is where all promotion CRUD now goes as payment-old is retired.
const ADMIN_BASE = '/admin/promotions';

// REST responses use Mongo's `_id`; normalize to the `id` shape the UI expects,
// including on nested options.
const normalizeOption = (o) => ({ ...o, id: o._id ?? o.id });
const normalizePromotion = (p) =>
  p
    ? {
        ...p,
        id: p._id ?? p.id,
        options: (p.options || []).map(normalizeOption),
      }
    : p;

// Surface the server's error message (e.g. duplicate code) instead of axios's
// generic "Request failed with status code 400".
const messageFrom = (e) =>
  e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Request failed';

// Codeless referral-config records share the promotions collection (the schema
// only requires a code for credit/coupon promos). This page manages redeemable
// promo codes, so anything without a code is dropped rather than shown blank.
const hasCode = (p) => !!p.code;

// Server-side paginated listing for the admin promotions page. The nestjs admin
// service sorts newest-first and does the codeless filtering, search, and
// active/inactive filtering, returning { data, count, counts }:
//   - data:   the current page of promotions (newest first)
//   - count:  total matching the current search/status (drives pagination)
//   - counts: dataset-wide { all, active, inactive } totals for the filter chips
//
// Falls back gracefully if it ever gets a bare array response (e.g. an older
// backend): filter + paginate the array client-side so the page keeps working.
//
// `limit` defaults to the page's PAGE_SIZE (20); the caller passes it explicitly,
// but keeping the default aligned avoids mismatched page math for future callers.
const getPromotions = async ({ offset = 0, limit = 20, search = '', status = 'all' } = {}) => {
  const params = new URLSearchParams();
  params.set('offset', String(offset));
  params.set('limit', String(limit));
  if (search) params.set('search', search);
  if (status && status !== 'all') params.set('status', status);

  const { data } = await get(`${ADMIN_BASE}?${params.toString()}`);

  if (Array.isArray(data)) {
    // Legacy backend: no server-side pagination. Reproduce the paginated shape
    // from the full array so callers don't care which backend answered.
    const isDeactivated = (p) => !!p.endsOn && new Date(p.endsOn) <= new Date();
    const all = data.filter(hasCode);
    const q = search.trim().toLowerCase();
    const matches = all.filter((p) => {
      if (status === 'active' && isDeactivated(p)) return false;
      if (status === 'inactive' && !isDeactivated(p)) return false;
      if (!q) return true;
      return [p.code, p.description, p.subtitle].filter(Boolean).some((v) => v.toLowerCase().includes(q));
    });
    const inactive = all.filter(isDeactivated).length;
    return {
      data: matches.slice(offset, offset + limit).map(normalizePromotion),
      count: matches.length,
      counts: { all: all.length, active: all.length - inactive, inactive },
    };
  }

  return {
    data: (data?.data || []).map(normalizePromotion),
    count: data?.count ?? 0,
    counts: data?.counts ?? { all: 0, active: 0, inactive: 0 },
  };
};

const getPromotion = async ({ id }) => {
  const { data } = await get(`${ADMIN_BASE}/${id}`);
  return normalizePromotion(data);
};

const createOrUpdatePromotion = async ({ input }) => {
  const { id, ...body } = input;
  try {
    const { data } = id ? await put(`${ADMIN_BASE}/${id}`, body) : await post(ADMIN_BASE, body);
    return normalizePromotion(data);
  } catch (e) {
    throw new Error(messageFrom(e));
  }
};

// Soft-disable: set endsOn to now so the promo is immediately unredeemable,
// without destroying the record or anyone's existing attachment/history.
const deactivatePromotion = async ({ id }) => {
  try {
    const { data } = await put(`${ADMIN_BASE}/${id}`, { endsOn: new Date().toISOString() });
    return normalizePromotion(data);
  } catch (e) {
    throw new Error(messageFrom(e));
  }
};

// Permanently remove a promotion.
const deletePromotion = async ({ id }) => {
  try {
    await del(`${ADMIN_BASE}/${id}`);
  } catch (e) {
    throw new Error(messageFrom(e));
  }
};

// Per-promotion usage summary for the Usage tab: how many accounts the code is
// attached to (applied) and how many uses have actually been consumed on rides
// (redeemed). Computed fresh server-side from the users collection, so it
// reflects the live state rather than a possibly-stale list count. Lives on the
// nestjs admin service alongside the promotions list + add-to-user endpoints.
const getPromotionUsage = async ({ id }) => {
  try {
    const { data } = await get(`${ADMIN_BASE}/${id}/usage`);
    return {
      appliedCount: Number(data?.appliedCount) || 0,
      redeemedCount: Number(data?.redeemedCount) || 0,
    };
  } catch (e) {
    throw new Error(messageFrom(e));
  }
};

// Paginated, searchable list of the accounts a promotion is attached to, for
// the Usage tab's Accounts table. Returns { data, count }; each row includes a
// `redeemed` flag so the table can distinguish who has used the code from who
// merely has it. Search matches name / email / phone server-side.
const getPromotionAccounts = async ({ id, search = '', offset = 0, limit = 25 } = {}) => {
  const params = new URLSearchParams();
  params.set('offset', String(offset));
  params.set('limit', String(limit));
  if (search) params.set('search', search);
  try {
    const { data } = await get(`${ADMIN_BASE}/${id}/accounts?${params.toString()}`);
    return {
      data: (data?.data || []).map((u) => ({ ...u, id: u.id ?? u._id })),
      count: data?.count ?? 0,
    };
  } catch (e) {
    throw new Error(messageFrom(e));
  }
};

// Attach an existing promotion to a specific user's account (admin only).
// This lives on the nestjs admin service (not payment-old); response is
// { promotionId, alreadyExists }.
const addPromotionToUser = async ({ userId, promotionId }) => {
  try {
    const { data } = await post('/admin/promotions/add-to-user', { userId, promotionId });
    return { alreadyExists: !!data?.alreadyExists };
  } catch (e) {
    throw new Error(messageFrom(e));
  }
};

export default {
  getPromotions,
  getPromotion,
  createOrUpdatePromotion,
  deactivatePromotion,
  deletePromotion,
  getPromotionUsage,
  getPromotionAccounts,
  addPromotionToUser,
};
